import { useCallback, useEffect } from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import { Button, isLoggedIn, Link, SpinnerCenter, TOTPCodeInput } from "@shapr3d/frontend-lib";
import { Controller, useForm } from "react-hook-form";
import { z } from "zod";

import { FormWrapper } from "../components/FormWrapper/FormWrapper";
import Heading from "../components/Heading/Heading";
import { useLoginCredentials } from "../contexts/LoginCredentialsContext";
import useNavigation from "../hooks/navigation";
import useAccount from "../hooks/useAccount";
import { useLogin } from "../hooks/useAuth";
import { useLoginRedirect } from "../hooks/useLoginRedirect";
import useTranslation from "../hooks/useTranslation";
import { AuthError } from "../types";

import style from "./TwoFactorVerifyPage.module.css";

const verificationCodeSchema = z.object({
  code: z.string().length(6, "Code must be 6 digits"),
});

type VerificationFormData = z.infer<typeof verificationCodeSchema>;

export const TwoFactorVerifyPage = () => {
  const navigation = useNavigation();
  const { t, m } = useTranslation();
  const { credentials: loginCredentials, setCredentials } = useLoginCredentials();

  const { mutateAsync: mutateLogin, isPending: isLoginPending } = useLogin();

  useAccount();
  const redirectCallback = useLoginRedirect();

  const {
    setError,
    clearErrors,
    formState: { errors, isSubmitting, isSubmitted, isValid },
    control,
    handleSubmit,
  } = useForm<VerificationFormData>({
    resolver: zodResolver(verificationCodeSchema),
    reValidateMode: "onSubmit",
    defaultValues: {
      code: "",
    },
  });

  useEffect(() => {
    if (isLoggedIn()) {
      redirectCallback();
      return;
    }

    if (!loginCredentials) {
      navigation.goToSignIn();
    }
  }, [redirectCallback, navigation, loginCredentials]);

  const onSubmit = useCallback(
    async (data: VerificationFormData) => {
      if (!loginCredentials) {
        navigation.goToSignIn();
        return;
      }

      try {
        await mutateLogin(
          {
            email: loginCredentials.email,
            password: loginCredentials.password,
            twoFactorTotpCode: data.code,
          },
          {
            onSuccess: () => {
              redirectCallback();
              setCredentials(null);
            },
            onError: (error) => {
              if (error === AuthError.InvalidEmailPassword) {
                setError("code", { message: "Invalid verification code." });
              } else {
                setError("code", { message: "Something went wrong. Please try again." });
              }
            },
          }
        );
      } catch (_) {
        // Error is handled by onError callback
      }
    },
    [loginCredentials, mutateLogin, redirectCallback, navigation, setCredentials, setError]
  );

  const isLoading = isLoginPending || isSubmitting;

  if (!loginCredentials) {
    return <SpinnerCenter />;
  }

  return (
    <FormWrapper>
      <div className={style.twoFactorLoginForm}>
        <Heading type="h1">{t(m.TwoFactorAuth.Title)}</Heading>

        <form className={style.content} onSubmit={handleSubmit(onSubmit)} noValidate>
          {t(m.TwoFactorAuth.EnterCode)}
          <div className={style.inputContainer}>
            <Controller
              name="code"
              control={control}
              render={({ field }) => (
                <TOTPCodeInput
                  onChange={(value) => {
                    field.onChange(value);
                    clearErrors("code");
                  }}
                  onComplete={(value) => onSubmit({ code: value })}
                  error={Boolean(errors.code)}
                  errorMessage={errors.code?.message}
                  disabled={isLoading}
                  autoFocus
                />
              )}
            />
            <div>
              {t(m.TwoFactorAuth.DontHaveApp)}
              {""}
              <Link onClick={() => navigation.goToTwoFactorRecovery({ replace: true })}>
                {t(m.TwoFactorAuth.Reset2FA)}
              </Link>
            </div>
          </div>
          <Button
            type="submit"
            loading={isLoading}
            aria-label="continue"
            disabled={!isValid && isSubmitted}
          >
            {t(m.General.Continue)}
          </Button>
        </form>
      </div>
    </FormWrapper>
  );
};
